import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import './EmailVerificationForm.css';

interface EmailVerificationFormProps {
  onSuccess?: (email: string) => void;
  onSendOtp: (email: string) => Promise<void>;
  onVerifyOtp: (email: string, otp: string) => Promise<void>;
  onCheckEmail: (email: string) => Promise<{ taken: boolean; message?: string }>;
}

// List of translation keys used for errors
const knownErrorKeys = [
  'invalidEmail',
  'otpSentSuccess',
  'invalidOtp',
  'emailVerifiedSuccess',
  // add more as needed
] as const;
type ErrorTranslationKey = typeof knownErrorKeys[number];
function isTranslationKey(key: string): key is ErrorTranslationKey {
  return (knownErrorKeys as readonly string[]).includes(key);
}

const RESEND_OTP_SECONDS = 60;

const EmailVerificationForm: React.FC<EmailVerificationFormProps> = ({ onSuccess, onSendOtp, onVerifyOtp, onCheckEmail }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [editingEmail, setEditingEmail] = useState(false);

  // Timer effect
  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resendTimer]);

  // Email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Real-time email validation
  useEffect(() => {
    if (!email && emailTouched) {
      setEmailError('Email is required');
    } else if (email && !isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  }, [email, emailTouched]);

  // Handle OTP input changes
  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (e.target.value.length > 1) return; // Only allow single digit
    
    const newOtp = [...otp];
    newOtp[index] = e.target.value;
    setOtp(newOtp);

    // Auto-focus next input
    if (e.target.value && index < 5) {
      const nextInput = otpRefs.current[index + 1];
      nextInput?.focus();
    }
  };

  // Handle backspace in OTP input
  const handleOtpKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = otpRefs.current[index - 1];
      prevInput?.focus();
    }
  };

  // Send OTP API call
  const sendOtp = async () => {
    setEmailTouched(true);
    if (!email || !isValidEmail(email)) {
      setEmailError(!email ? 'Email is required' : 'Please enter a valid email address');
      showMessage(t('invalidEmail'), 'error');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      // Check if email is already taken
      const check = await onCheckEmail(email);
      if (check.taken) {
        showMessage(check.message || 'This email is already registered. Please use a different email.', 'error');
        setIsLoading(false);
        return;
      }
      await onSendOtp(email);
      setOtpSent(true);
      setEditingEmail(false);
      setShowOtpInput(true);
      setResendTimer(RESEND_OTP_SECONDS);
      showMessage(t('otpSentSuccess'), 'success');
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'Failed to send OTP. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP API call
  const verifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      showMessage(t('invalidOtp'), 'error');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      await onVerifyOtp(email, otpString);
      showMessage(t('emailVerifiedSuccess'), 'success');
      setTimeout(() => {
        onSuccess?.(email);
      }, 2000);
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'Invalid OTP. Please try again.', 'error');
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const resendOtp = async () => {
    setOtp(['', '', '', '', '', '']);
    setResendTimer(RESEND_OTP_SECONDS);
    await sendOtp();
  };

  // Show message with animation
  const showMessage = (msg: string, type: 'success' | 'error') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  // When edit is clicked, allow editing email and reset OTP state
  const handleEditEmail = () => {
    setEditingEmail(true);
    setOtp(['', '', '', '', '', '']);
    setShowOtpInput(false);
    setOtpSent(false);
    setEmailError('');
  };

  return (
    <div className="email-verification-container">
      <div className="form-card">
        <h2>{t('emailVerificationTitle')}</h2>
        <div className="form-description">{t('emailVerificationDesc')}</div>
        {/* Message Display (moved up) */}
        {message && (
          <div className={`message ${messageType}`} style={{ marginBottom: showOtpInput ? '1.5rem' : 0 }}>
            <span className="message-icon">
              {messageType === 'success' ? '✓' : '✗'}
            </span>
            {message}
          </div>
        )}

        {/* Email Input Section */}
        {!showOtpInput && (
          <div className="email-section">
            <div className="input-group">
              <input
                id="email"
                className={`email-input${emailError ? ' invalid' : ''}`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={otpSent && !editingEmail}
                placeholder={t('emailPlaceholder')}
              />
              <button
                className="send-otp-btn"
                onClick={sendOtp}
                disabled={otpSent && !editingEmail || !email}
                type="button"
              >
                {t('sendOtp')}
              </button>
            </div>
            {emailError && (
              <div className="input-error">
                {isTranslationKey(emailError) ? t(emailError) : emailError}
              </div>
            )}
          </div>
        )}

        {/* OTP Input Section */}
        {otpSent && !editingEmail && (
          <div className="otp-section">
            <div className="otp-instruction">
              <span>{t('enterOtp')}</span>
              <div style={{marginTop: '0.7rem', fontWeight: 600, color: '#007bff', fontSize: '1.08rem'}}>
                {email}
                <button
                  type="button"
                  style={{marginLeft: 8, background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', fontSize: '1.1em'}}
                  onClick={handleEditEmail}
                  aria-label={t('editEmail')}
                >✏️</button>
              </div>
            </div>
            
            <div className="otp-inputs">
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  className="otp-input"
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(e, idx)}
                  onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                  ref={(el) => (otpRefs.current[idx] = el)}
                  disabled={isLoading}
                />
              ))}
            </div>

            <div className="otp-actions">
              <button
                className="verify-otp-btn"
                onClick={verifyOtp}
                disabled={isLoading}
                type="button"
              >
                {isLoading ? (
                  <div className="spinner"></div>
                ) : (
                  t('verify')
                )}
              </button>
              
              <button
                className="resend-otp-btn"
                onClick={resendOtp}
                disabled={isLoading || resendTimer > 0}
                type="button"
              >
                {resendTimer > 0 ? `${t('resend')} (${resendTimer}s)` : t('resend')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationForm; 